import type { ReactNode } from 'react';
import { DateField } from '../primitives';
import { PickOrCreate } from '../primitives/PickOrCreate';
import { PropertyPill } from '../primitives/PropertyPill';
import {
  formatPropertyPillLabel,
  isPropertyPillMuted
} from '../primitives/propertyBarDisplay';
import {
  formatDatePillValue,
  NO_EPIC_VALUE,
  TYPE_LABEL,
  TYPE_LOCK_TOOLTIP,
  type EpicOption
} from './workItemCanvasPropertyBarShared';
import type { OrgProject } from '../../lib/projects/useOrgProjects';
import type { Database } from '../../types/database';

type WorkItemType = Database['public']['Enums']['work_item_type'];

const NO_PROJECT_VALUE = '__no_project__';

export interface WorkItemCanvasBuiltInPillsProps {
  workItemType: WorkItemType;
  workItemStatus: string;
  workItemDueDate: string | null;
  workItemStartDate: string | null;
  workItemEndDate: string | null;
  parentWorkItemId: string | null;
  parentName: string | null;
  statusStages: ReadonlyArray<string>;
  epicOptions: EpicOption[];
  workItemProjectId: string | null;
  projectOptions: OrgProject[];
  dateError: string | null;
  editable: boolean;
  testIdPrefix: string;
  commitStatus: (next: string) => Promise<void>;
  commitDue: (next: string | null) => Promise<void>;
  commitStart: (next: string | null) => Promise<void>;
  commitEnd: (next: string | null) => Promise<void>;
  commitParent: (next: string | null) => Promise<void>;
  commitProject: (next: string | null) => Promise<void>;
}

function noopPopover() {
  return null;
}

function DatePopoverBody({
  dateError,
  children
}: {
  dateError: string | null;
  children: ReactNode;
}) {
  return (
    <>
      {children}
      {dateError ? (
        <div className="wid-property-bar-date-error" role="alert">
          {dateError}
        </div>
      ) : null}
    </>
  );
}

export function WorkItemCanvasBuiltInPills({
  workItemType,
  workItemStatus,
  workItemDueDate,
  workItemStartDate,
  workItemEndDate,
  parentWorkItemId,
  parentName,
  statusStages,
  epicOptions,
  workItemProjectId,
  projectOptions,
  dateError,
  editable,
  testIdPrefix,
  commitStatus,
  commitDue,
  commitStart,
  commitEnd,
  commitParent,
  commitProject
}: WorkItemCanvasBuiltInPillsProps) {
  const typeLabel = TYPE_LABEL[workItemType] ?? workItemType;

  const isChildItem = parentWorkItemId !== null;
  const projectPickOptions = [
    { id: NO_PROJECT_VALUE, label: '— No project —' },
    ...projectOptions.map((p) => ({ id: p.id, label: p.name }))
  ];
  const projectSelectValue = workItemProjectId ?? NO_PROJECT_VALUE;
  const projectDisplay =
    workItemProjectId == null
      ? null
      : (projectOptions.find((p) => p.id === workItemProjectId)?.name ?? null);

  const statusPickOptions = statusStages.map((s) => ({
    id: s,
    label: s
  }));
  if (!statusStages.includes(workItemStatus) && workItemStatus) {
    statusPickOptions.unshift({ id: workItemStatus, label: workItemStatus });
  }
  const statusDisplay = workItemStatus || null;

  const epicPickOptions = [
    { id: NO_EPIC_VALUE, label: '— No epic —' },
    ...epicOptions.map((e) => ({
      id: e.id,
      label: e.name || '(untitled)'
    }))
  ];
  const epicSelectValue = parentWorkItemId ?? NO_EPIC_VALUE;
  const epicDisplay =
    parentWorkItemId == null
      ? null
      : (epicOptions.find((e) => e.id === parentWorkItemId)?.name ??
        '(untitled)');

  const showEpicPicker =
    workItemType !== 'epic' && workItemType !== 'subtask';

  return (
    <>
      <span title={TYPE_LOCK_TOOLTIP}>
        <PropertyPill
          label="Type"
          displayValue={typeLabel}
          editable={false}
          testId={`${testIdPrefix}-pill-type`}
        >
          {noopPopover}
        </PropertyPill>
      </span>

      {showEpicPicker ? (
        epicOptions.length === 0 ? (
          <PropertyPill
            label="Epic"
            displayValue={null}
            editable={false}
            testId={`${testIdPrefix}-pill-epic`}
          >
            {noopPopover}
          </PropertyPill>
        ) : (
          <PickOrCreate
            id="work-item-canvas-bar-epic"
            label="Epic"
            options={epicPickOptions}
            value={{ option_id: epicSelectValue }}
            disabled={!editable}
            triggerVariant="pill"
            pillLabel={formatPropertyPillLabel('Epic', epicDisplay)}
            pillMuted={isPropertyPillMuted(epicDisplay)}
            data-testid={`${testIdPrefix}-pill-epic`}
            onPick={(picked) => {
              if (!picked) return;
              void commitParent(
                picked.id === NO_EPIC_VALUE ? null : picked.id
              );
            }}
            onCreate={async () => null}
          />
        )
      ) : workItemType === 'subtask' ? (
        <PropertyPill
          label="Parent"
          displayValue={parentName}
          editable={false}
          testId={`${testIdPrefix}-pill-parent`}
        >
          {noopPopover}
        </PropertyPill>
      ) : null}

      <PickOrCreate
        id="work-item-canvas-bar-status"
        label="Status"
        options={statusPickOptions}
        value={workItemStatus ? { option_id: workItemStatus } : null}
        disabled={!editable}
        triggerVariant="pill"
        pillLabel={formatPropertyPillLabel('Status', statusDisplay)}
        pillMuted={isPropertyPillMuted(statusDisplay)}
        data-testid={`${testIdPrefix}-pill-status`}
        onPick={(picked) => {
          if (picked) void commitStatus(picked.id);
        }}
        onCreate={async () => null}
      />

      {isChildItem ? (
        <PropertyPill
          label="Project"
          displayValue={projectDisplay}
          editable={false}
          testId={`${testIdPrefix}-pill-project`}
        >
          {noopPopover}
        </PropertyPill>
      ) : projectOptions.length === 0 ? (
        <PropertyPill
          label="Project"
          displayValue={null}
          editable={false}
          testId={`${testIdPrefix}-pill-project`}
        >
          {noopPopover}
        </PropertyPill>
      ) : (
        <PickOrCreate
          id="work-item-canvas-bar-project"
          label="Project"
          options={projectPickOptions}
          value={{ option_id: projectSelectValue }}
          disabled={!editable}
          triggerVariant="pill"
          pillLabel={formatPropertyPillLabel('Project', projectDisplay)}
          pillMuted={isPropertyPillMuted(projectDisplay)}
          data-testid={`${testIdPrefix}-pill-project`}
          onPick={(picked) => {
            if (!picked) return;
            void commitProject(
              picked.id === NO_PROJECT_VALUE ? null : picked.id
            );
          }}
          onCreate={async () => null}
        />
      )}

      {workItemType === 'epic' ? (
        <>
          <PropertyPill
            label="Start"
            displayValue={formatDatePillValue(workItemStartDate)}
            editable={editable}
            testId={`${testIdPrefix}-pill-start`}
          >
            {() => (
              <DatePopoverBody dateError={dateError}>
                <DateField
                  id="work-item-canvas-bar-start"
                  label="Start"
                  hideLabel
                  value={workItemStartDate}
                  onChange={(next) => void commitStart(next)}
                  testId="work-item-canvas-bar-start-input"
                />
              </DatePopoverBody>
            )}
          </PropertyPill>

          <PropertyPill
            label="End"
            displayValue={formatDatePillValue(workItemEndDate)}
            editable={editable}
            testId={`${testIdPrefix}-pill-end`}
          >
            {() => (
              <DatePopoverBody dateError={dateError}>
                <DateField
                  id="work-item-canvas-bar-end"
                  label="End"
                  hideLabel
                  value={workItemEndDate}
                  onChange={(next) => void commitEnd(next)}
                  testId="work-item-canvas-bar-end-input"
                />
              </DatePopoverBody>
            )}
          </PropertyPill>
        </>
      ) : null}

      <PropertyPill
        label="Due"
        displayValue={formatDatePillValue(workItemDueDate)}
        editable={editable}
        testId={`${testIdPrefix}-pill-due`}
      >
        {() => (
          <DateField
            id="work-item-canvas-bar-due"
            label="Due"
            hideLabel
            value={workItemDueDate}
            onChange={(next) => void commitDue(next)}
            testId="work-item-canvas-bar-due-input"
          />
        )}
      </PropertyPill>
    </>
  );
}
